import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BasketPair } from '../services/api';

export type ServerOrder = 'lift' | 'support' | 'confidence' | 'co_count';

interface Props {
  pairs: BasketPair[];
  loading?: boolean;
  /** Métrica pela qual o servidor ordenou (e truncou) o conjunto recebido. */
  serverOrder?: ServerOrder;
  /** Pede novo fetch ordenado por outra métrica. Sem isto, a ordenação é local. */
  onServerOrderChange?: (order: ServerOrder) => void;
}

type SortKey =
  | 'sku_a' | 'sku_b' | 'co_count' | 'support'
  | 'confidence_a_to_b' | 'confidence_b_to_a' | 'lift' | 'jaccard';
type SortDir = 'asc' | 'desc';

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'sku_a', label: 'SKU A', numeric: false },
  { key: 'sku_b', label: 'SKU B', numeric: false },
  { key: 'co_count', label: 'Co-Count', numeric: true },
  { key: 'support', label: 'Support', numeric: true },
  { key: 'confidence_a_to_b', label: 'Conf A→B', numeric: true },
  { key: 'confidence_b_to_a', label: 'Conf B→A', numeric: true },
  { key: 'lift', label: 'Lift', numeric: true },
  { key: 'jaccard', label: 'Jaccard', numeric: true },
];

// Colunas que o backend sabe ordenar. As restantes (SKUs, jaccard) só têm
// ordenação local — o servidor devolve um top-N por uma destas métricas, logo
// re-ordenar localmente por uma coluna sem equivalente mostra apenas o máximo
// DENTRO do conjunto já truncado, não o topo global.
const SERVER_ORDER_BY_COLUMN: Partial<Record<SortKey, ServerOrder>> = {
  co_count: 'co_count',
  support: 'support',
  lift: 'lift',
  confidence_a_to_b: 'confidence',
  confidence_b_to_a: 'confidence',
};

const fmtPct = (v: number | null): string => {
  if (v === null || v === undefined) return '-';
  return (v * 100).toFixed(2) + '%';
};

const fmtNum = (v: number | null, digits = 2): string => {
  if (v === null || v === undefined) return '-';
  return v.toFixed(digits);
};

// Badge para produto não-online (offline / descontinuado no site)
const offlineBadge = (online: boolean | undefined): JSX.Element | null =>
  online === false
    ? <span className="badge bg-warning text-dark ms-1" title="Produto não está online">offline</span>
    : null;

// Célula SKU + nome completo do produto
const skuCell = (sku: string, name: string | null | undefined, online?: boolean): JSX.Element => (
  <span>
    <Link to={`/products/${sku}`} className="text-decoration-none">
      <span className="fw-semibold">{sku}</span>
      {name ? <span className="text-muted ms-1">· {name}</span> : null}
    </Link>
    {offlineBadge(online)}
  </span>
);

const liftBadge = (lift: number | null): JSX.Element => {
  if (lift === null || lift === undefined) return <span className="text-muted">-</span>;
  let cls = 'badge bg-secondary';
  if (lift >= 5) cls = 'badge bg-success';
  else if (lift >= 2.5) cls = 'badge bg-primary';
  else if (lift >= 1.5) cls = 'badge bg-info text-dark';
  return <span className={cls}>{lift.toFixed(2)}x</span>;
};

const compare = (a: BasketPair, b: BasketPair, key: SortKey, numeric: boolean): number => {
  const va = a[key];
  const vb = b[key];
  // nulls sempre no fim
  if (va === null || va === undefined) return 1;
  if (vb === null || vb === undefined) return -1;
  if (numeric) return (va as number) - (vb as number);
  return String(va).localeCompare(String(vb));
};

const BasketPairsTable: React.FC<Props> = ({ pairs, loading, serverOrder, onServerOrderChange }) => {
  const [sortKey, setSortKey] = useState<SortKey>('co_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    const target = SERVER_ORDER_BY_COLUMN[key];
    // Coluna com equivalente no servidor e ordem diferente da actual → refetch,
    // para o topo mostrado ser o topo real e não o máximo dos N já carregados.
    if (target && onServerOrderChange && target !== serverOrder) {
      setSortKey(key);
      setSortDir('desc');
      onServerOrderChange(target);
      return;
    }
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // numéricas começam desc (maior primeiro), texto asc
      const col = COLUMNS.find((c) => c.key === key);
      setSortDir(col && col.numeric ? 'desc' : 'asc');
    }
  };

  // Ordenação puramente local sobre um conjunto truncado pelo servidor: avisar.
  const localOnlySort = !!serverOrder
    && SERVER_ORDER_BY_COLUMN[sortKey] !== serverOrder;

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey);
    const numeric = !!(col && col.numeric);
    const dir = sortDir === 'asc' ? 1 : -1;
    // Sinal aplicado ao comparador (nulls sempre no fim, fora do factor de direcção) → estável
    return [...pairs].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      return dir * compare(a, b, sortKey, numeric);
    });
  }, [pairs, sortKey, sortDir]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status" />
        <p className="mt-2 text-muted">A carregar pares...</p>
      </div>
    );
  }

  if (pairs.length === 0) {
    return (
      <div className="alert alert-info">
        Sem pares para os critérios actuais. Tenta baixar o <strong>min lift</strong> ou o <strong>min support</strong>.
      </div>
    );
  }

  const arrow = (key: SortKey) => {
    if (key !== sortKey) return <span className="text-muted ms-1">↕</span>;
    return <span className="ms-1">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="table-responsive">
      {localOnlySort && (
        <div className="alert alert-warning py-1 px-2 small mb-2">
          Ordenação aplicada apenas aos <strong>{pairs.length}</strong> pares carregados
          (o servidor devolveu o top por <strong>{serverOrder}</strong>). Não é o topo global
          desta coluna.
        </div>
      )}
      <table className="table table-sm table-hover align-middle">
        <thead className="table-light">
          <tr>
            <th>#</th>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                className={`user-select-none ${c.numeric ? 'text-end' : ''}`}
                aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-reset fw-bold"
                  onClick={() => handleSort(c.key)}
                  title={SERVER_ORDER_BY_COLUMN[c.key] && onServerOrderChange
                    ? 'Clicar para reordenar no servidor (novo pedido)'
                    : 'Clicar para ordenar os resultados carregados'}
                >
                  {c.label}{arrow(c.key)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((p, idx) => (
            <tr key={`${p.sku_a}_${p.sku_b}`}>
              <td className="text-muted">{idx + 1}</td>
              <td>{skuCell(p.sku_a, p.name_a, p.online_a)}</td>
              <td>{skuCell(p.sku_b, p.name_b, p.online_b)}</td>
              <td className="text-end">{p.co_count.toLocaleString('pt-PT')}</td>
              <td className="text-end">{fmtPct(p.support)}</td>
              <td className="text-end">{fmtPct(p.confidence_a_to_b)}</td>
              <td className="text-end">{fmtPct(p.confidence_b_to_a)}</td>
              <td className="text-end">{liftBadge(p.lift)}</td>
              <td className="text-end">{fmtNum(p.jaccard, 3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BasketPairsTable;
